<template>
  <!--
    「요금·결제」의 구독 탭(설계안 2판 §10.1 · 선결제 §21.7) — 구독 목록과 거르기(상품·상태·요금제·결제 방식·검색어). 행을 누르면(또는 「상세」) 부모가
    상세 창을 연다. 말·거르기는 `utils/subscriptionAdmin` 한 곳 — 여기는 조회와 그리기만.
  -->
  <div data-testid="subscription-tab">
    <div class="search_form" ref="search_form">
      <slot name="tabs"></slot>
      <p class="form-field-hint subscription__intro">{{ $t(text.intro) }}</p>
      <el-form :inline="true" :size="size" label-suffix=":" @submit.native.prevent>
        <el-form-item :label="$t(text.filterProduct)">
          <el-select
            v-model="filter.product"
            data-testid="subscription-filter-product"
            :style="{ width: '130px' }"
            @change="searchFirstPage">
            <el-option :label="$t(text.filterAll)" value=""></el-option>
            <el-option
              v-for="option in productOptions"
              :key="option.value"
              :label="$t(option.label)"
              :value="option.value"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item :label="$t(text.filterStatus)">
          <el-select
            v-model="filter.status"
            data-testid="subscription-filter-status"
            :style="{ width: '110px' }"
            @change="searchFirstPage">
            <el-option :label="$t(text.filterAll)" value=""></el-option>
            <el-option
              v-for="option in statusOptions"
              :key="option.value"
              :label="$t(option.label)"
              :value="option.value"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item :label="$t(text.filterPlan)">
          <el-select
            v-model="filter.planCode"
            data-testid="subscription-filter-plan"
            :style="{ width: '130px' }"
            @change="searchFirstPage">
            <el-option :label="$t(text.filterAll)" value=""></el-option>
            <el-option
              v-for="option in planOptions"
              :key="option.value"
              :label="$t(option.label)"
              :value="option.value"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item :label="$t(text.filterTerm)">
          <el-select
            v-model="filter.termMonths"
            data-testid="subscription-filter-term"
            :style="{ width: '130px' }"
            @change="searchFirstPage">
            <el-option :label="$t(text.filterAll)" value=""></el-option>
            <el-option
              v-for="option in termOptions"
              :key="option.value"
              :label="$t(option.label)"
              :value="option.value"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item :label="$t(text.filterQuery)">
          <el-input
            v-model="filter.q"
            clearable
            data-testid="subscription-filter-q"
            :placeholder="$t(text.filterQueryPlaceholder)"
            :style="{ width: $device.mobile ? `100%` : `300px` }"
            @keyup.enter.native="searchFirstPage"></el-input>
        </el-form-item>
      </el-form>
    </div>

    <div class="buttons" ref="buttons">
      <el-button :size="size" icon="el-icon-search" @click="searchFirstPage">{{ $t('검색') }}</el-button>
      <!-- 행이 아직 없는 매장(새 Lite 를 쓴 적 없는 매장)도 지정한다 — 매장은 창에서 고른다(3판 §20.5) -->
      <el-button
        v-if="canGrant"
        :size="size"
        type="primary"
        plain
        icon="el-icon-plus"
        data-testid="subscription-grant-new"
        @click="grantVisible = true"
        >{{ $t(text.grantStore) }}</el-button
      >
      <!-- 행이 아직 없는 매장에도 체험을 준다 — 매장은 창에서 고른다(§25.4) -->
      <el-button
        v-if="canTrialStart"
        :size="size"
        plain
        icon="el-icon-plus"
        data-testid="subscription-store-trial-new"
        @click="storeTrialVisible = true"
        >{{ $t(text.storeTrial) }}</el-button
      >
    </div>

    <SubscriptionGrantDialog :visible.sync="grantVisible" @granted="search" />
    <SubscriptionStoreTrialDialog :visible.sync="storeTrialVisible" @started="search" @changed="search" />

    <el-table
      :height="tableHeight - 48"
      border
      ref="table"
      row-key="subscriptionId"
      :data="tableData"
      :size="size"
      :empty-text="$t(text.subscriptionsEmpty)"
      :default-sort="{ prop: defaultSort.sortProp, order: defaultSort.sortOrder }"
      :row-class-name="canOpen ? 'subscription-row--open' : ''"
      @row-click="open"
      @sort-change="handleSortChange">
      <!--
        핵심 칸이 앞이다 — 상태·매장·요금제와 결제 방식·결제 금액·지금 기능(합 약 640px)이 1024×768 의 표 폭 안에 들고, 날짜 셋은 그 뒤(넘치면
        스크롤). 요금제와 결제 방식, 결제 금액과 월 정가는 한 칸 두 줄이다. 오더엔 멀티는 결제 방식 아래에 사업자 수·보너스 줄이 더 있다(§26.7)
      -->
      <el-table-column :label="$t(text.columnStatus)" width="130">
        <template slot-scope="scope">
          <div class="subscription-marks" data-testid="subscription-status">
            <SubscriptionBadge :tone="subscriptionStatusViewOf(scope.row).tone">{{
              $t(subscriptionStatusViewOf(scope.row).label)
            }}</SubscriptionBadge>
            <SubscriptionBadge
              v-for="mark in subscriptionStatusViewOf(scope.row).marks"
              :key="mark.label"
              :tone="mark.tone"
              outline
              >{{ $t(mark.label) }}</SubscriptionBadge
            >
          </div>
        </template>
      </el-table-column>
      <el-table-column :label="$t(text.columnStore)" min-width="140">
        <template slot-scope="scope">
          <!-- 누구의 구독인가 — POS2 Lite 는 매장(번호 · 가입 경로), 오더엔 멀티는 사장님(「사장님」 · 휴대폰) -->
          <div class="subscription-store" data-testid="subscription-subscriber">
            {{ subscriberCellOf(scope.row, [signupPathText(scope.row)]).name }}
          </div>
          <div class="subscription-store__id" data-testid="subscription-store-id">
            {{
              subscriberCellOf(scope.row, [signupPathText(scope.row)])
                .tags.map((tag) => $t(tag))
                .join(' · ')
            }}
          </div>
        </template>
      </el-table-column>
      <el-table-column :label="`${$t(text.columnPlan)} · ${$t(text.columnTerm)}`" width="170">
        <template slot-scope="scope">
          <div>{{ scope.row.planName || scope.row.planCode || '—' }}</div>
          <div class="subscription-store__id" data-testid="subscription-term">{{ termText(scope.row) }}</div>
          <div
            v-for="line in multiLinesOf(scope.row)"
            :key="line"
            class="subscription-store__id"
            data-testid="subscription-multi">
            {{ line }}
          </div>
        </template>
      </el-table-column>
      <el-table-column :label="$t(text.columnPrice)" min-width="110" align="right">
        <template slot-scope="scope">
          <div data-testid="subscription-price">{{ priceLinesOf(scope.row).main || '—' }}</div>
          <div class="subscription-store__id">{{ priceLinesOf(scope.row).list }}</div>
        </template>
      </el-table-column>
      <el-table-column :label="$t(text.columnFeatures)" width="80" align="center">
        <template slot-scope="scope">
          <span data-testid="subscription-features">{{ featureTierOf(scope.row) }}</span>
        </template>
      </el-table-column>
      <el-table-column :label="$t(text.columnTrialEnd)" prop="trialEndDtm" width="110" align="center" sortable="custom">
        <template slot-scope="scope">{{ dateText(scope.row.trialEndDtm) || '—' }}</template>
      </el-table-column>
      <el-table-column :label="$t(text.columnApplied)" prop="appliedDtm" width="110" align="center" sortable="custom">
        <template slot-scope="scope">{{ dateText(scope.row.appliedDtm) || '—' }}</template>
      </el-table-column>
      <el-table-column :label="$t(text.columnCanceled)" width="110" align="center">
        <template slot-scope="scope">{{ dateText(scope.row.canceledDtm) || '—' }}</template>
      </el-table-column>
    </el-table>

    <list-pagination
      :search-form="searchForm"
      :total="totalElements"
      @page-change="handlePageChange"
      @size-change="handleSizeChange"></list-pagination>
  </div>
</template>

<script>
import SubscriptionBadge from '@/components/subscription/SubscriptionBadge.vue'
import SubscriptionGrantDialog from '@/components/subscription/SubscriptionGrantDialog.vue'
import SubscriptionStoreTrialDialog from '@/components/subscription/SubscriptionStoreTrialDialog.vue'
import mainVueMixin from '@/mixins/mainVueMixin'
import pagedListMixin from '@/mixins/pagedListMixin'
import { initData } from '@/utils/constants'
import { withPaging } from '@/utils/pagedList'
import {
  SUBSCRIPTION_ADMIN_ACTION,
  SUBSCRIPTION_ADMIN_API,
  SUBSCRIPTION_DEFAULT_SORT,
  SUBSCRIPTION_PLAN_OPTIONS,
  SUBSCRIPTION_PRODUCT_OPTIONS,
  SUBSCRIPTION_STATUS_OPTIONS,
  SUBSCRIPTION_TEXT,
  dateText,
  featureTierOf,
  multiLinesOf,
  priceLinesOf,
  signupPathText,
  subscriberCellOf,
  subscriptionListQuery,
  subscriptionStatusViewOf,
  termOptionsOf,
  termText,
} from '@/utils/subscriptionAdmin'

export default {
  name: 'SubscriptionTable',

  mixins: [mainVueMixin, pagedListMixin],

  components: { SubscriptionBadge, SubscriptionGrantDialog, SubscriptionStoreTrialDialog },

  data: function () {
    var data = initData()
    data.url = SUBSCRIPTION_ADMIN_API
    data.text = SUBSCRIPTION_TEXT
    data.productOptions = SUBSCRIPTION_PRODUCT_OPTIONS
    data.statusOptions = SUBSCRIPTION_STATUS_OPTIONS
    data.planOptions = SUBSCRIPTION_PLAN_OPTIONS
    data.defaultSort = SUBSCRIPTION_DEFAULT_SORT
    data.searchForm = Object.assign(withPaging({}), SUBSCRIPTION_DEFAULT_SORT)
    /** 거르기 — 빈 글자는 전체. 결제 방식은 개월 수(0 = 무약정)로 고른다 */
    data.filter = { product: '', status: '', planCode: '', termMonths: '', q: '' }
    /** [매장 지정] 창 */
    data.grantVisible = false
    /** [매장 체험] 창 */
    data.storeTrialVisible = false

    return data
  },

  computed: {
    /** 결제 방식 거르기의 선택지 — 상품 거르기가 오더엔 멀티면 멀티의 이름(0 개월 「월 결제」) */
    termOptions: function () {
      return termOptionsOf(this.filter.product)
    },
    /** 상세 창을 열 수 있는가 — 상세 조회의 액션 */
    canOpen: function () {
      return this.checkAuthActionAccess(SUBSCRIPTION_ADMIN_ACTION.GET)
    },
    /** [매장 지정]을 둘 것인가 — 지정 액션 */
    canGrant: function () {
      return this.checkAuthActionAccess(SUBSCRIPTION_ADMIN_ACTION.GRANT)
    },
    /** [매장 체험]을 둘 것인가 — 매장 체험 액션 */
    canTrialStart: function () {
      return this.checkAuthActionAccess(SUBSCRIPTION_ADMIN_ACTION.TRIAL_START)
    },
  },

  created: function () {
    this.searchFirstPage()
  },

  methods: {
    dateText,
    featureTierOf,
    multiLinesOf,
    priceLinesOf,
    signupPathText,
    subscriberCellOf,
    subscriptionStatusViewOf,
    termText,

    search: function () {
      this.mainVue.$axios.search(
        this.url,
        subscriptionListQuery(this.filter, this.searchQuery()),
        function (result) {
          this.applyPage(result)
        }.bind(this)
      )
    },

    open: function (row) {
      if (this.canOpen) this.$emit('open', row.subscriptionId)
    },
  },
}
</script>

<style scoped>
.subscription__intro {
  margin: 0 0 8px;
}

.subscription-marks {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.subscription-store {
  font-weight: 500;
  color: var(--color-text-primary);
}

.subscription-store__id {
  font-size: 12px;
  color: var(--color-text-secondary);
}

.el-table >>> .subscription-row--open {
  cursor: pointer;
}
</style>
